import { PluginContext } from '../../../mol-plugin/context';
import { PluginState } from '../../../mol-plugin/state';
import { WebMcpActivityLog } from '../activity';
import { WebMcpProposalStore } from '../proposals';
import { WebMcpActor, WebMcpProvenanceArgs, WebMcpProvenanceLog } from '../provenance';
import { ToolDefinition, ToolResponse } from '../registration';
import { ExpandResultCache, failure, success, toMcpResult } from '../result';

export interface WebMcpToolSuccess {
    readonly ok: true
    readonly summary: string
}

export interface WebMcpToolFailure {
    readonly ok: false
    readonly error: string
    readonly hint: string
}

export type WebMcpToolOutcome = WebMcpToolSuccess | WebMcpToolFailure;

export interface WebMcpMutation {
    readonly actor: WebMcpActor
    readonly tool: string
    readonly args: WebMcpProvenanceArgs
    readonly summary: string
}

export interface WebMcpMutationSnapshotStore {
    captureBeforeChange(): void
}

/** Per-viewer runtime passed to mutating tool factories. */
export interface WebMcpMutationServices {
    readonly provenance: WebMcpProvenanceLog
    readonly activity: WebMcpActivityLog
    readonly undoRedo: WebMcpMutationSnapshotStore
}

export interface WebMcpSafetyServices extends WebMcpMutationServices {
    readonly proposals: WebMcpProposalStore
    readonly undoRedo: WebMcpUndoRedoState
    readonly expandResults: ExpandResultCache
}

export interface WebMcpUndoResult {
    readonly ok: boolean
    readonly summary: string
    readonly hint?: string
}

/** Keeps full Mol* plugin snapshots, rather than partial tool-specific state. */
export class WebMcpUndoRedoState {
    private readonly undoSnapshots: PluginState.Snapshot[] = [];
    private readonly redoSnapshots: PluginState.Snapshot[] = [];

    constructor(private readonly plugin: PluginContext) {}

    captureBeforeChange() {
        this.undoSnapshots.push(this.snapshot());
        this.redoSnapshots.length = 0;
    }

    async undo(): Promise<WebMcpUndoResult> {
        const snapshot = this.undoSnapshots.pop();
        if (!snapshot) return { ok: false, summary: 'Nothing to undo.', hint: 'Make a reversible viewer change first.' };
        try {
            this.redoSnapshots.push(this.snapshot());
            await this.plugin.state.setSnapshot(snapshot);
            return { ok: true, summary: 'Undid the last viewer change.' };
        } catch (error) {
            this.undoSnapshots.push(snapshot);
            return { ok: false, summary: error instanceof Error ? error.message : 'Undo failed.', hint: 'The viewer could not restore its previous snapshot.' };
        }
    }

    async redo(): Promise<WebMcpUndoResult> {
        const snapshot = this.redoSnapshots.pop();
        if (!snapshot) return { ok: false, summary: 'Nothing to redo.', hint: 'Undo a viewer change first.' };
        try {
            this.undoSnapshots.push(this.snapshot());
            await this.plugin.state.setSnapshot(snapshot);
            return { ok: true, summary: 'Redid the last viewer change.' };
        } catch (error) {
            this.redoSnapshots.push(snapshot);
            return { ok: false, summary: error instanceof Error ? error.message : 'Redo failed.', hint: 'The viewer could not restore its next snapshot.' };
        }
    }

    private snapshot(): PluginState.Snapshot {
        return this.plugin.state.getSnapshot({
            data: true,
            behavior: true,
            structureSelection: true,
            componentManager: true,
            animation: true,
            canvas3d: true,
            canvas3dContext: true,
            interactivity: true,
            camera: true,
            cameraTransition: { name: 'instant', params: {} },
        });
    }
}

/** Runs a reversible mutation once and records its provenance without throwing through MCP. */
interface ProposalIdInput {
    readonly proposalId: string
}

interface LimitInput {
    readonly limit: number
}

interface ExpandResultInput {
    readonly cursor: string
}

interface RawProposalIdInput {
    readonly proposalId?: unknown
}

interface RawLimitInput {
    readonly limit?: unknown
}

interface RawExpandResultInput {
    readonly cursor?: unknown
}

function isRawProposalIdInput(input: unknown): input is RawProposalIdInput {
    return input !== null && typeof input === 'object';
}

function isRawLimitInput(input: unknown): input is RawLimitInput {
    return input !== null && typeof input === 'object';
}

function isRawExpandResultInput(input: unknown): input is RawExpandResultInput {
    return input !== null && typeof input === 'object';
}

function isNonEmptyString(value: unknown): value is string {
    return typeof value === 'string' && value.trim() !== '';
}

function isLimit(value: unknown): value is number {
    return typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 100;
}

export function parseProposalIdInput(input: unknown): ProposalIdInput | undefined {
    if (!isRawProposalIdInput(input) || !isNonEmptyString(input.proposalId)) return;
    return { proposalId: input.proposalId.trim() };
}

export function parseLimitInput(input: unknown): LimitInput | undefined {
    if (!isRawLimitInput(input)) return { limit: 20 };
    if (input.limit === void 0) return { limit: 20 };
    return isLimit(input.limit) ? { limit: input.limit } : void 0;
}

export function parseExpandResultInput(input: unknown): ExpandResultInput | undefined {
    return isRawExpandResultInput(input) && isNonEmptyString(input.cursor) ? { cursor: input.cursor.trim() } : void 0;
}

function unavailable(error: string, hint: string): ToolResponse {
    return toMcpResult(failure(error, hint));
}

function proposalResponse(result: Awaited<ReturnType<WebMcpProposalStore['apply']>>) {
    return result.ok
        ? toMcpResult(success(`Proposal ${result.proposal.id} ${result.proposal.status}.`, { type: 'proposal', proposal: result.proposal }))
        : unavailable(result.error, result.hint);
}

/** Tool definitions for proposal review, provenance, activity, and snapshot history. */
export function createSafetyTools(services: WebMcpSafetyServices): readonly ToolDefinition[] {
    return [
        {
            name: 'expand-result',
            description: 'Expand a truncated WebMCP result using its cursor.',
            inputSchema: { type: 'object', properties: { cursor: { type: 'string', minLength: 1 } }, required: ['cursor'], additionalProperties: false },
            annotations: { readOnlyHint: true },
            isAvailable: () => services.expandResults.hasResults(),
            execute: input => {
                const parsed = parseExpandResultInput(input);
                if (!parsed) return unavailable('Invalid result cursor.', 'Provide a cursor returned in a truncated result.');
                const page = services.expandResults.expand(parsed.cursor);
                if (!page) return unavailable('Result cursor was not found.', 'Request the original result again; only the three most recent results are retained.');
                return page.serialized
                    ? { content: [{ type: 'text', text: page.serialized }] }
                    : toMcpResult(success('Expanded result returned.', { type: 'expanded-result', items: page.items }, page.truncation));
            }
        },
        {
            name: 'get-pending-proposals',
            description: 'List destructive WebMCP proposals waiting to be applied or rejected.',
            inputSchema: { type: 'object' },
            annotations: { readOnlyHint: true },
            execute: () => toMcpResult(success('Pending proposals returned.', { type: 'proposals', proposals: services.proposals.getPending() }))
        },
        {
            name: 'apply-proposal',
            description: 'Apply one pending destructive proposal after revalidation.',
            inputSchema: { type: 'object', properties: { proposalId: { type: 'string', minLength: 1 } }, required: ['proposalId'] },
            annotations: { destructiveHint: true },
            execute: async input => {
                const parsed = parseProposalIdInput(input);
                if (!parsed) return unavailable('Invalid proposal ID.', 'Provide a non-empty proposalId from get-pending-proposals.');
                try { return proposalResponse(await services.proposals.apply(parsed.proposalId)); } catch (error) {
                    return unavailable(error instanceof Error ? error.message : 'Could not apply proposal.', 'Check the viewer state, then retry.');
                }
            }
        },
        {
            name: 'reject-proposal',
            description: 'Reject one pending destructive proposal without changing the viewer.',
            inputSchema: { type: 'object', properties: { proposalId: { type: 'string', minLength: 1 } }, required: ['proposalId'] },
            annotations: { idempotentHint: true },
            execute: input => {
                const parsed = parseProposalIdInput(input);
                if (!parsed) return unavailable('Invalid proposal ID.', 'Provide a non-empty proposalId from get-pending-proposals.');
                try { return proposalResponse(services.proposals.reject(parsed.proposalId)); } catch (error) {
                    return unavailable(error instanceof Error ? error.message : 'Could not reject proposal.', 'Use get-pending-proposals and retry.');
                }
            }
        },
        {
            name: 'get-provenance',
            description: 'List accepted WebMCP mutation provenance.',
            inputSchema: { type: 'object', properties: { limit: { type: 'integer', minimum: 1, maximum: 100 } } },
            annotations: { readOnlyHint: true },
            execute: input => {
                const parsed = parseLimitInput(input);
                return parsed ? toMcpResult(success('Provenance returned.', { type: 'provenance', entries: services.provenance.list(parsed.limit) })) : unavailable('Invalid limit.', 'Use an integer from 1 to 100.');
            }
        },
        {
            name: 'get-user-activity',
            description: 'List recent semantic viewer activity.',
            inputSchema: { type: 'object', properties: { limit: { type: 'integer', minimum: 1, maximum: 100 } } },
            annotations: { readOnlyHint: true },
            execute: input => {
                const parsed = parseLimitInput(input);
                return parsed ? toMcpResult(success('User activity returned.', { type: 'activity', entries: services.activity.list(parsed.limit) })) : unavailable('Invalid limit.', 'Use an integer from 1 to 100.');
            }
        },
        {
            name: 'undo',
            description: 'Restore the full Mol* snapshot before the last reversible WebMCP mutation.',
            inputSchema: { type: 'object' },
            execute: async () => {
                const result = await services.undoRedo.undo();
                return result.ok ? toMcpResult(success(result.summary, { type: 'history', action: 'undo' })) : unavailable(result.summary, result.hint ?? 'Make a reversible viewer change first.');
            }
        },
        {
            name: 'redo',
            description: 'Restore the full Mol* snapshot reversed by undo.',
            inputSchema: { type: 'object' },
            execute: async () => {
                const result = await services.undoRedo.redo();
                return result.ok ? toMcpResult(success(result.summary, { type: 'history', action: 'redo' })) : unavailable(result.summary, result.hint ?? 'Undo a viewer change first.');
            }
        },
    ];
}

export async function runWebMcpMutation(
    mutation: WebMcpMutation,
    services: WebMcpMutationServices,
    execute: () => Promise<void>,
): Promise<WebMcpToolOutcome> {
    const endAgentMutation = services.activity.beginAgentMutation();
    try {
        services.undoRedo.captureBeforeChange();
        await execute();
        services.provenance.add(mutation.actor, mutation.tool, mutation.args, mutation.summary);
        return { ok: true, summary: mutation.summary };
    } catch (error) {
        return { ok: false, error: error instanceof Error ? error.message : 'Viewer mutation failed.', hint: 'Check that the requested structure or selection still exists, then retry.' };
    } finally {
        endAgentMutation();
    }
}
